/**
 * Main-thread proxies for the persistence worker.
 *
 * Every main-thread module that needs to encode, decode, or persist a
 * `SceneDocument` should go through one of these wrappers — never call
 * `persistenceWorkerClient.ts` directly. Keeping the surface narrow makes
 * the worker boundary easy to reason about and easy to mock in tests.
 */

import {
  workerDecodeDocument,
  workerEncodeDocument,
  workerLoadSession,
  workerSaveSceneCache,
  workerSaveSession,
} from "./persistenceWorkerClient.ts";
import type { RawSceneDocument, RawSessionDocument } from "./types.ts";

/** Encode + JSON.stringify a scene document for `.pr` file save. */
export async function serializeSceneDocument(
  raw: RawSceneDocument,
): Promise<string> {
  const { json } = await workerEncodeDocument(raw);
  return json;
}

/** JSON.parse + decode a `.pr` file body into a raw scene document. */
export async function parseSceneDocument(
  json: string,
): Promise<RawSceneDocument> {
  const { raw } = await workerDecodeDocument(json);
  return raw;
}

/** Encode + write a scene to the IndexedDB cache. */
export async function saveSceneCache(
  key: string,
  raw: RawSceneDocument,
): Promise<{ sizeBytes: number }> {
  return workerSaveSceneCache(key, raw);
}

/** Encode + write the session restore document. */
export async function saveSessionCache(
  raw: RawSessionDocument,
): Promise<{ sizeBytes: number }> {
  return workerSaveSession(raw);
}

/** Read the session document, returning the decoded form and the raw JSON. */
export async function loadSessionCache(): Promise<{
  raw: RawSessionDocument | null;
  json: string | null;
}> {
  return workerLoadSession();
}
